<script setup lang="ts">
    import { Button } from '../../Button';
    import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components';
    import { useDialogs } from '../dialogs.store';
    import { InputField } from '../../../components/form-fields/InputField';
    import { Form } from 'vee-validate';
    import { TextareaField } from '@/shared/components/form-fields/TextareaField';

    const props = defineProps<{
        title: string;
        description?: string;
        cancelLabel?: string;
        confirmLabel?: string;
        initialValue?: string;
        label?: string;
        placeholder?: string;
        validationRules?: string;
        inputHint?: string;
        textArea?: boolean;

        onConfirm?: (value: string) => Promise<any>;
    }>();

    const dialogsStore = useDialogs();
    const { t } = useI18n();
    const isLoading = ref(false);
    const inputValue = ref(props.initialValue ?? '');

    const inputComponent = computed(() => {
        return props.textArea ? TextareaField : InputField;
    });

    async function handleSubmit() {
        if (props.onConfirm) {
            isLoading.value = true;
            await props.onConfirm(inputValue.value).finally(() => (isLoading.value = false));
        }

        dialogsStore.close(inputValue.value);
    }
</script>

<template>
    <Dialog default-open>
        <DialogContent>
            <Form @submit="handleSubmit">
                <DialogHeader>
                    <DialogTitle>{{ title }}</DialogTitle>
                    <DialogDescription class="sr-only">{{ description ?? t('DIALOG_DESCRIPTION') }}</DialogDescription>
                </DialogHeader>

                <div class="py-4">
                    <component
                        :is="inputComponent"
                        v-model="inputValue"
                        name="myinput"
                        :label="label"
                        :description="inputHint"
                        :placeholder="placeholder"
                        :rules="validationRules"
                    />
                </div>

                <DialogFooter>
                    <DialogClose as-child>
                        <Button
                            variant="tertiary"
                            type="button"
                        >
                            {{ cancelLabel ?? t('GLOBAL.CANCEL') }}
                        </Button>
                    </DialogClose>

                    <Button
                        variant="brand"
                        :disabled="isLoading"
                        :loading="isLoading"
                        type="submit"
                    >
                        {{ confirmLabel ?? t('GLOBAL.CONFIRM') }}
                    </Button>
                </DialogFooter>
            </Form>
        </DialogContent>
    </Dialog>
</template>

<i18n lang="yaml">
en:
    DIALOG_DESCRIPTION: Please enter a value
nl:
    DIALOG_DESCRIPTION: Voer een waarde in
</i18n>
